import React, { useState } from 'react';
import { Play, ChevronLeft, ChevronRight, BookOpen, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { RelatedFormula, Step } from '../../types';
import { highlightThinkingWords } from '../../utils/textHelpers';
import { Button } from '../ui';

interface StepCardProps {
  steps: Step[];
  currentStep: number;
  isSpeaking: boolean;
  relatedFormulas: RelatedFormula[];
  verification?: string;
  onStepChange: (step: number) => void;
  onSpeak: () => void;
}

export const StepCard: React.FC<StepCardProps> = ({
  steps,
  currentStep,
  isSpeaking,
  relatedFormulas,
  verification,
  onStepChange,
  onSpeak,
}) => {
  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const [isFormulasOpen, setIsFormulasOpen] = useState(false);

  const shouldShowVerification =
    isLastStep && !!verification?.trim() && verification !== 'N/A';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex-1 flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-sm">
              {currentStep + 1}
            </div>
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                Step {currentStep + 1} of {steps.length}
              </div>
              <h2 className="text-sm font-semibold text-gray-900">{step?.title}</h2>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {relatedFormulas && relatedFormulas.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                icon={<BookOpen className="w-4 h-4" />}
                iconPosition="left"
                onClick={() => setIsFormulasOpen(v => !v)}
                title="Related formulas"
              >
                {isFormulasOpen ? (
                  <span className="flex items-center gap-1">
                    Formulas
                    <ChevronUp className="w-4 h-4" />
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    Formulas
                    <ChevronDown className="w-4 h-4" />
                  </span>
                )}
              </Button>
            )}
            <Button
              variant={isSpeaking ? 'secondary' : 'ghost'}
              size="sm"
              icon={<Play className={`w-4 h-4 ${isSpeaking ? 'animate-pulse' : ''}`} />}
              onClick={onSpeak}
              disabled={isSpeaking}
              title="Read aloud"
            />
          </div>
        </div>
      </div>

      {isFormulasOpen && relatedFormulas && relatedFormulas.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="space-y-2">
            {relatedFormulas.map((f, idx) => (
              <div key={idx}>
                <div className="text-xs font-semibold text-gray-900">{f.name}</div>
                <div className="font-mono text-xs text-gray-900 bg-white px-2 py-1 rounded mt-0.5 border border-gray-200">
                  {f.formula}
                </div>
                {f.description?.trim() && (
                  <div className="text-[11px] text-gray-600 mt-0.5">{f.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="space-y-2">
          {step?.why?.trim() && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-[10px] text-blue-700 uppercase tracking-wide font-semibold mb-1">Why</div>
              <p
                className="text-sm text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: highlightThinkingWords(step.why) }}
              />
            </div>
          )}

          {step?.action?.trim() && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-[10px] text-blue-700 uppercase tracking-wide font-semibold mb-1">Action</div>
              <p
                className="text-sm text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: highlightThinkingWords(step.action) }}
              />
            </div>
          )}

          {step?.result?.trim() && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-[10px] text-blue-700 uppercase tracking-wide font-semibold mb-1">Result</div>
              <p
                className="text-sm text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: highlightThinkingWords(step.result) }}
              />
            </div>
          )}

          {shouldShowVerification && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[10px] text-blue-700 uppercase tracking-wide font-semibold mb-1">Verification</div>
                  <p className="text-sm text-gray-800 leading-relaxed">{verification}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Math for this step */}
        {step?.math && step.math.length > 0 && (
          <div className="border-l-3 border-l-blue-400 pl-4 space-y-1" style={{ borderLeftWidth: '3px' }}>
            {step.math.map((expr, idx) => (
              <div key={idx} className="font-mono text-gray-700 text-base">
                {expr}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t border-gray-100">
        <Button
          variant="secondary"
          size="sm"
          icon={<ChevronLeft className="w-4 h-4" />}
          onClick={() => onStepChange(currentStep - 1)}
          disabled={isFirstStep}
        >
          Back
        </Button>

        {/* Step Indicators */}
        <div className="flex gap-1">
          {steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => onStepChange(idx)}
              className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                idx === currentStep 
                  ? 'bg-blue-500' 
                  : idx < currentStep 
                    ? 'bg-blue-300' 
                    : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        <Button
          variant="secondary"
          size="sm"
          icon={<ChevronRight className="w-4 h-4" />}
          iconPosition="right"
          onClick={() => onStepChange(currentStep + 1)}
          disabled={isLastStep}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default StepCard;
